"use client";

import {
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Settings,
  User,
} from "lucide-react";
import { lazy, Suspense, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationBell } from "@/components/notifications/notification-bell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { logout } from "@/store/modules/users/users.actions";
import { useSelector, useStore } from "react-redux";
import { selectCurrentUser } from "@/store/modules/users/users.selectors";
import { useCanManageWorkspace } from "@/hooks/use-has-capability";

const LazyMyProfileModal = lazy(() =>
  import("./my-profile-modal/my-profile.modal").then((module) => ({
    default: module.MyProfileModal,
  }))
);

const LazyManageWorkspaceModal = lazy(() =>
  import("./manage-workspace-modal/manage-workspace.modal").then((module) => ({
    default: module.ManageWorkspaceModal,
  }))
);

export function NavUser() {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false);

  const store = useStore();

  const user = useSelector(selectCurrentUser);
  const canManageWorkspace = useCanManageWorkspace();

  if (!user?.id) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem className="flex justify-center group-data-[collapsible=icon]:px-0">
        <NotificationBell align="start" />
      </SidebarMenuItem>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="w-full justify-between">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user.data.avatar} alt={user.data.firstName} />
                <AvatarFallback>
                  {user.data.firstName.charAt(0)}
                  {user.data.lastName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium">
                  {user.data.firstName} {user.data.lastName}
                </span>
                <span className="truncate whitespace-nowrap text-xs text-muted-foreground">
                  {user.credentials.email}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user.data.firstName} {user.data.lastName}
                </p>
                <p className="truncate whitespace-nowrap text-xs leading-none text-muted-foreground">
                  {user.credentials.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={() => setIsProfileModalOpen(true)}>
                <User className="mr-2 h-4 w-4" />
                <span>My Profile</span>
              </DropdownMenuItem>
              {canManageWorkspace && (
                <DropdownMenuItem
                  onSelect={() => setIsWorkspaceModalOpen(true)}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Manage Workspace</span>
                </DropdownMenuItem>
              )}
              {canManageWorkspace && (
                <DropdownMenuItem disabled>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>Billing</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                store.dispatch(logout());
              }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      {isProfileModalOpen && (
        <Suspense fallback={null}>
          <LazyMyProfileModal
            isOpen={isProfileModalOpen}
            onClose={() => setIsProfileModalOpen(false)}
          />
        </Suspense>
      )}
      {isWorkspaceModalOpen && (
        <Suspense fallback={null}>
          <LazyManageWorkspaceModal
            isOpen={isWorkspaceModalOpen}
            onClose={() => setIsWorkspaceModalOpen(false)}
          />
        </Suspense>
      )}
    </SidebarMenu>
  );
}
